// Receipt identity stays separate from actions; invoice edits and versioned AUD valuation keep their existing flows.
import { useState } from 'react';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/workspace';
import { AliasFormModal, type AliasPrefill } from '@/components/AliasFormModal';
import { BackButton } from '@/components/BackButton';
import { CategoryHint } from '@/components/CategoryHint';
import { usePageBack } from '@/hooks/usePageBack';
import { safeReturnTo } from '@/lib/appNavigation';
import { AskAiButton } from '@/components/chat/AskAiButton';
import { ReceiptValuationCard, type ValuedReceipt } from '@/components/ReceiptValuationCard';
import { FixedBookingMatch } from '@/components/advisor/FixedBookingMatch';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

interface ReceiptRow extends ValuedReceipt {
  booking_evidence?: string | null;
  source?: string | null;
  merged_into_receipt_id?: string | null;
  id: string;
  merchant: string | null;
  merchant_zh: string | null;
  receipt_date: string | null;
  receipt_time: string | null;
  currency: string;
  subtotal: number | null;
  tax_amount: number | null;
  total_amount: number;
  category_id: string | null;
  image_path: string | null;
  image_count?: number;
  mime_types?: string[];
  receipt_number: string | null;
  ai_model: string | null;
  ai_confidence: number | null;
  extraction_status: 'pending' | 'success' | 'failed' | 'manual';
  extraction_error: string | null;
  is_verified: number;
  /** 0/1 — surfaces as a toggle for the AU tax-deductions tracker. */
  is_tax_deductible?: number;
  notes: string | null;
  recorded_by: string;
  spender_user_id: string | null;
  alias?: { id: string; display_name: string } | null;
}

interface DuplicateMatch {
  id: string;
  merchant: string | null;
  receipt_date: string | null;
  total_amount: number;
  receipt_number: string | null;
  matched_by: 'receipt_number' | 'composite';
}

interface ItemRow {
  id: string;
  line_no: number | null;
  name: string;
  name_normalized: string | null;
  name_zh: string | null;
  quantity: number;
  unit_price: number | null;
  subtotal: number;
  is_discount: number;
  category_id: string | null;
}

interface EditForm {
  merchant: string;
  merchant_zh: string;
  receipt_date: string;
  receipt_time: string;
  currency: string;
  total_amount: string;
  subtotal: string;
  tax_amount: string;
  category_id: string;
  spender_user_id: string;
}

interface CategoryRow {
  id: string;
  name: string;
  name_zh: string | null;
  icon: string | null;
  color: string | null;
  is_system: number;
  sort_order: number;
}

interface MemberRow {
  id: string;
  email: string;
  name: string | null;
  role: 'owner' | 'member';
}

interface ItemFormValues {
  name: string;
  quantity: number;
  unit_price: number | null;
  subtotal: number;
  is_discount: boolean;
}

function toItemPayload(v: Partial<ItemFormValues>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (v.name !== undefined) payload.name = v.name;
  if (v.quantity !== undefined) payload.quantity = v.quantity;
  if (v.unit_price !== undefined) payload.unit_price = v.unit_price;
  if (v.subtotal !== undefined) payload.subtotal = v.subtotal;
  if (v.is_discount !== undefined) payload.is_discount = v.is_discount;
  return payload;
}

function ItemFormRow({
  initial,
  saveLabel,
  onSave,
  onCancel,
  onDelete,
  isPending,
}: {
  initial: ItemRow | null;
  saveLabel: string;
  onSave: (data: ItemFormValues) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isPending?: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [quantity, setQuantity] = useState(String(initial?.quantity ?? 1));
  const [unitPrice, setUnitPrice] = useState(
    initial?.unit_price != null ? String(initial.unit_price) : '',
  );
  const [subtotal, setSubtotal] = useState(
    initial?.subtotal != null ? String(initial.subtotal) : '',
  );
  const [isDiscount, setIsDiscount] = useState(!!initial?.is_discount);
  const [error, setError] = useState<string | null>(null);

  function handleSave() {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t('receipt.itemNameRequired'));
      return;
    }
    const q = Number(quantity);
    if (!Number.isFinite(q) || q <= 0) {
      setError(t('receipt.itemQtyPositive'));
      return;
    }
    const s = Number(subtotal);
    if (!Number.isFinite(s)) {
      setError(t('receipt.itemSubtotalNumber'));
      return;
    }
    const upRaw = unitPrice.trim();
    let up: number | null = null;
    if (upRaw !== '') {
      const v = Number(upRaw);
      if (!Number.isFinite(v)) {
        setError(t('receipt.itemUnitPriceNumber'));
        return;
      }
      up = v;
    }
    onSave({
      name: trimmedName,
      quantity: q,
      unit_price: up,
      subtotal: s,
      is_discount: isDiscount,
    });
  }

  return (
    <div className="space-y-2 rounded-md bg-muted/30 p-2">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('receipt.itemNamePlaceholder')}
        className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
      />
      <div className="grid grid-cols-3 gap-1">
        <label className="block text-xs">
          <span className="block text-muted-foreground">{t('receipt.itemQuantity')}</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full rounded-md border bg-background px-2 py-1 text-sm"
          />
        </label>
        <label className="block text-xs">
          <span className="block text-muted-foreground">{t('receipt.itemUnitPrice')}</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            placeholder={t('receipt.itemUnitPriceOptional')}
            className="w-full rounded-md border bg-background px-2 py-1 text-sm"
          />
        </label>
        <label className="block text-xs">
          <span className="block text-muted-foreground">{t('receipt.itemSubtotal')}</span>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={subtotal}
            onChange={(e) => setSubtotal(e.target.value)}
            className="w-full rounded-md border bg-background px-2 py-1 text-sm"
          />
        </label>
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={isDiscount}
          onChange={(e) => setIsDiscount(e.target.checked)}
        />
        <span>{t('receipt.itemDiscountLabel')}</span>
      </label>
      {error && <div className="text-xs text-destructive">{error}</div>}
      <div className="flex gap-2 pt-1">
        <button
          onClick={onCancel}
          disabled={isPending}
          className="flex-1 rounded-md border bg-background py-1 text-xs hover:bg-muted disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={isPending}
          className="flex-1 rounded-md bg-primary py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {isPending ? t('receipt.saving') : saveLabel}
        </button>
        {onDelete && (
          <button
            onClick={async () => {
              if (await confirmDialog({ title: t('receipt.deleteItem'), destructive: true })) onDelete();
            }}
            disabled={isPending}
            className="rounded-md border border-destructive/40 bg-background px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
            aria-label={t('receipt.deleteItem')}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

export function ReceiptDetailPage() {
  const { id } = useParams<{ id: string }>();

  const location = useLocation();
  const returnParam = new URLSearchParams(location.search).get('returnTo');
  const returnTo = safeReturnTo(returnParam) ?? '/ledger';
  const goBack = usePageBack(returnTo);
  const ws = useWorkspaceStore((s) => s.current);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Capture-only state: Capture.tsx sets `dupUpload` when an upload was
  // short-circuited because the same image bytes were already on file. Read
  // once on mount so re-rendering doesn't keep re-showing the banner.
  const dupUploadState = location.state as
    | { dupUpload?: boolean; merchant?: string | null }
    | null;
  const [dupBannerVisible, setDupBannerVisible] = useState<boolean>(
    !!dupUploadState?.dupUpload,
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['receipts', id, ws?.id],
    queryFn: () =>
      api.get<{
        receipt: ReceiptRow;
        items: ItemRow[];
        potential_duplicates?: DuplicateMatch[];
      }>(`/receipts/${id}`),
    enabled: !!id,
  });

  const { data: categories } = useQuery({
    queryKey: ['categories', ws?.id],
    queryFn: () => api.get<CategoryRow[]>('/categories'),
    enabled: !!ws,
    staleTime: 5 * 60 * 1000,
  });

  const { data: members } = useQuery({
    queryKey: ['members', ws?.id],
    queryFn: () => api.get<MemberRow[]>(`/workspaces/${ws!.id}/members`),
    enabled: !!ws,
    staleTime: 5 * 60 * 1000,
  });

  const [showImages, setShowImages] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [addingItem, setAddingItem] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const addItemMutation = useMutation({
    mutationFn: (body: ItemFormValues) =>
      api.post<{ item: ItemRow }>(`/receipts/${id}/items`, toItemPayload(body)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['receipts', id] });
      setAddingItem(false);
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Add item failed');
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: Partial<ItemFormValues> }) =>
      api.patch<{ ok: true }>(`/receipts/${id}/items/${itemId}`, toItemPayload(body)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['receipts', id] });
      setEditingItemId(null);
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Save item failed');
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) =>
      api.del<{ ok: true }>(`/receipts/${id}/items/${itemId}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['receipts', id] });
      setEditingItemId(null);
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Delete item failed');
    },
  });

  interface AliasLearnedPayload {
    id: string;
    source_key: string;
    display_name: string;
    display_name_zh: string | null;
    default_category_id: string | null;
    backfillable_count: number;
  }
  interface PatchResponse {
    ok: true;
    alias_learned?: AliasLearnedPayload;
  }
  const [aliasLearned, setAliasLearned] = useState<AliasLearnedPayload | null>(null);
  const [aliasPrefill, setAliasPrefill] = useState<AliasPrefill | null>(null);
  const [aliasToastState, setAliasToastState] = useState<'idle' | 'applied'>('idle');

  // Receipt edit (incl. merchant / category change → implicit alias learn)
  // cascades into every cache that aggregates over receipts. Same set of
  // affected prefixes as AliasFormModal — keep them in sync.
  const RECEIPT_DERIVED_PREFIXES = ['receipts', 'insights', 'advisor', 'goals', 'alerts'];
  const invalidateReceiptDerivedCaches = () =>
    queryClient.invalidateQueries({
      predicate: (q) => {
        if (!Array.isArray(q.queryKey)) return false;
        const head = q.queryKey[0];
        if (typeof head !== 'string') return false;
        return RECEIPT_DERIVED_PREFIXES.some((p) => head.startsWith(p));
      },
    });

  const patchMutation = useMutation<PatchResponse, Error, Record<string, unknown>>({
    mutationFn: (body) => api.patch<PatchResponse>(`/receipts/${id}`, body),
    onSuccess: async (res) => {
      await invalidateReceiptDerivedCaches();
      setEditing(false);
      if (res.alias_learned) {
        setAliasLearned(res.alias_learned);
        setAliasToastState('idle');
      }
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const aliasBackfillMutation = useMutation({
    mutationFn: () => {
      if (!aliasLearned) throw new Error('No alias to backfill');
      return api.post<{ updated: number }>(`/aliases/${aliasLearned.id}/backfill`, {});
    },
    onSuccess: async () => {
      setAliasToastState('applied');
      await invalidateReceiptDerivedCaches();
      setTimeout(() => setAliasLearned(null), 1500);
    },
  });

  const aliasUndoMutation = useMutation({
    mutationFn: () => {
      if (!aliasLearned) throw new Error('No alias to undo');
      return api.del<{ ok: true }>(`/aliases/${aliasLearned.id}`);
    },
    onSuccess: () => setAliasLearned(null),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.del<{ ok: true }>(`/receipts/${id}`),
    onSuccess: async () => {
      // Deleting a receipt removes its contribution from every aggregate
      // (insights totals, advisor savings, goal pace, alerts inputs). Use
      // the same broad-invalidation as edit so the user navigating back to
      // Home or Insights sees fresh numbers.
      await invalidateReceiptDerivedCaches();
      goBack();
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Delete failed');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        {t('receipt.loading')}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3">
        <p className="text-destructive">{t('receipt.loadFailed')}</p>
        <BackButton to="/ledger" />
      </div>
    );
  }

  const { receipt, items } = data;
  const duplicates = data.potential_duplicates ?? [];
  const currency = receipt.currency ?? ws?.default_currency ?? 'AUD';
  const confidencePct = receipt.ai_confidence != null ? Math.round(receipt.ai_confidence * 100) : null;
  const lowConfidence = confidencePct != null && confidencePct < 60;
  const failed = receipt.extraction_status === 'failed';
  const imageCount = receipt.image_count ?? (receipt.image_path ? 1 : 0);
  const mimeTypes = receipt.mime_types ?? [];
  const currentMime = mimeTypes[imgIdx] ?? 'image/jpeg';
  const isPdfSection = currentMime === 'application/pdf';

  const categoryById = (cid: string | null) =>
    cid ? categories?.find((c) => c.id === cid) ?? null : null;
  const memberById = (uid: string | null) =>
    uid ? members?.find((m) => m.id === uid) ?? null : null;
  const memberLabel = (m: MemberRow | null | undefined) =>
    m ? m.name?.trim() || m.email : '—';
  const categoryLabel = (c: CategoryRow | null | undefined) =>
    c ? `${c.icon ?? ''} ${c.name}`.trim() : '—';

  // When AI failed, show the image(s) prominently for manual correction.
  // When AI succeeded, hide them by default — user clicks "View originals" to verify.
  const imagesVisible = failed || showImages;

  function startEdit() {
    // If the receipt has no extracted time, default to "now" so the user can save
    // a time without having to type one. They can still clear it before saving.
    const nowHHMM = (() => {
      const d = new Date();
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    })();
    setForm({
      merchant: receipt.merchant ?? '',
      merchant_zh: receipt.merchant_zh ?? '',
      receipt_date: receipt.receipt_date ?? '',
      receipt_time: receipt.receipt_time ? receipt.receipt_time.slice(0, 5) : nowHHMM,
      currency: receipt.currency ?? ws?.default_currency ?? 'AUD',
      total_amount: String(receipt.total_amount ?? 0),
      subtotal: receipt.subtotal != null ? String(receipt.subtotal) : '',
      tax_amount: receipt.tax_amount != null ? String(receipt.tax_amount) : '',
      category_id: receipt.category_id ?? '',
      spender_user_id: receipt.spender_user_id ?? '',
    });
    setActionError(null);
    setEditing(true);
  }

  function saveEdit() {
    if (!form) return;
    setActionError(null);

    const body: Record<string, unknown> = {};
    body.merchant = form.merchant.trim() || null;
    body.merchant_zh = form.merchant_zh.trim() || null;
    body.receipt_date = form.receipt_date.trim() || null;
    body.receipt_time = form.receipt_time.trim() || null;

    const total = Number(form.total_amount);
    if (!Number.isFinite(total) || total < 0) {
      setActionError('Total must be a non-negative number');
      return;
    }
    body.total_amount = total;

    if (form.subtotal.trim() === '') body.subtotal = null;
    else {
      const v = Number(form.subtotal);
      if (!Number.isFinite(v)) {
        setActionError('Subtotal must be a number');
        return;
      }
      body.subtotal = v;
    }

    if (form.tax_amount.trim() === '') body.tax_amount = null;
    else {
      const v = Number(form.tax_amount);
      if (!Number.isFinite(v)) {
        setActionError('Tax must be a number');
        return;
      }
      body.tax_amount = v;
    }

    const currencyCode = form.currency.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(currencyCode)) {
      setActionError('Currency must be a 3-letter ISO code (e.g. AUD, USD)');
      return;
    }
    body.currency = currencyCode;

    body.category_id = form.category_id || null;
    body.spender_user_id = form.spender_user_id || null;

    // Once a user has filled out a previously-failed receipt manually, flip
    // its status so badges/banners reflect the new "manual entry" state and
    // the original image is hidden by default.
    if (receipt.extraction_status === 'failed') {
      body.extraction_status = 'manual';
    }

    patchMutation.mutate(body);
  }

  async function confirmDelete() {
    if (!(await confirmDialog({ title: t('receipt.deleteConfirm'), destructive: true }))) return;
    setActionError(null);
    deleteMutation.mutate();
  }

  return (
    <div className="min-h-screen bg-muted/30 pb-8">
      <header className="border-b bg-background">
        <div className="container max-w-md space-y-3 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <BackButton to="/ledger" className="shrink-0" />
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {failed && (
                <span className="rounded bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
                  {t('receipt.extractionFailed')}
                </span>
              )}
              {receipt.is_verified ? (
                <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                  ✓ {t('receipt.isVerified')}
                </span>
              ) : (
                lowConfidence && receipt.extraction_status === 'success' && (
                  <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                    {t('receipt.lowConfidence')}
                  </span>
                )
              )}
            </div>
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold leading-tight [overflow-wrap:anywhere]">
              {receipt.merchant ?? t('receipt.title')}
            </h1>
            {receipt.merchant_zh && (
              <div className="mt-1 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                {receipt.merchant_zh}
              </div>
            )}
            {receipt.alias ? (
              <Link
                to={`/aliases#alias-${receipt.alias.id}`}
                className="mt-1 inline-flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
              >
                ⚙ {t('receipt.aliasChip', { name: receipt.alias.display_name })} →
              </Link>
            ) : (
              receipt.merchant && (
                <button
                  type="button"
                  onClick={() =>
                    setAliasPrefill({
                      source_text: receipt.merchant ?? '',
                      display_name: receipt.merchant ?? '',
                      display_name_zh: receipt.merchant_zh,
                      default_category_id: receipt.category_id,
                    })
                  }
                  className="mt-1 inline-flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
                  title={t('receipt.aliasMakeHint')}
                >
                  {t('receipt.aliasMakeButton')}
                </button>
              )
            )}
          </div>
        </div>
      </header>

      <main className="container max-w-md space-y-4 py-4">
        <FixedBookingMatch key={receipt.id} receipt={receipt} />
        <ReceiptValuationCard key={receipt.id} receipt={receipt} />
        {failed && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            <div className="font-medium">{t('receipt.aiCouldntRead')}</div>
            {receipt.extraction_error && (
              <div className="mt-1 text-xs opacity-80">{receipt.extraction_error}</div>
            )}
            <div className="mt-2 text-xs">
              {imageCount > 1 ? t('receipt.enterManuallyPlural') : t('receipt.enterManually')}
            </div>
          </div>
        )}

        {dupBannerVisible && (
          <div className="rounded-md border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-100">
            <div className="flex items-start justify-between gap-2">
              <span>ℹ️ {t('receipt.dupUploadNotice')}</span>
              <button
                onClick={() => setDupBannerVisible(false)}
                className="rounded px-1.5 py-0.5 text-xs hover:bg-blue-100 dark:hover:bg-blue-900"
                aria-label={t('common.close')}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {duplicates.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="font-medium">
              ⚠️ {t('receipt.potentialDuplicates', { count: duplicates.length })}
            </div>
            <ul className="mt-2 space-y-1 text-xs">
              {duplicates.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2">
                  <Link
                    to={`/receipts/${d.id}`}
                    className="min-w-0 flex-1 line-clamp-2 underline hover:no-underline"
                  >
                    {d.merchant ?? t('home.unknownMerchant')}
                    {d.receipt_date ? ` · ${formatDate(d.receipt_date)}` : ''}
                    {' · '}
                    {formatCurrency(d.total_amount, currency)}
                  </Link>
                  <span className="rounded bg-amber-200 px-1.5 py-0.5 text-xs">
                    {d.matched_by === 'receipt_number'
                      ? `${t('receipt.receiptHash')}${d.receipt_number ?? ''}`
                      : t('receipt.matchedByComposite')}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-xs opacity-80">
              {t('receipt.duplicateDeleteHint')}
            </div>
          </div>
        )}

        {imagesVisible && imageCount > 0 && (
          <div className="overflow-hidden rounded-2xl border bg-card">
            {isPdfSection ? (
              <div className="bg-muted/30">
                <embed
                  src={`${API_BASE}/receipts/${receipt.id}/image?n=${imgIdx}`}
                  type="application/pdf"
                  className="block w-full"
                  style={{ height: '600px' }}
                />
                <div className="border-t px-3 py-2 text-xs">
                  <a
                    href={`${API_BASE}/receipts/${receipt.id}/image?n=${imgIdx}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline"
                  >
                    {t('receipt.openPdfNewTab')}
                  </a>
                </div>
              </div>
            ) : (
              <img
                src={`${API_BASE}/receipts/${receipt.id}/image?n=${imgIdx}`}
                alt={t('receipt.imageNav_n_of_total', { n: imgIdx + 1, total: imageCount })}
                className="w-full"
              />
            )}
            {imageCount > 1 && (
              <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                <button
                  onClick={() => setImgIdx((i) => Math.max(0, i - 1))}
                  disabled={imgIdx === 0}
                  className="rounded border px-2 py-1 disabled:opacity-30"
                >
                  {t('receipt.prevImage')}
                </button>
                <span className="text-muted-foreground">
                  {t('receipt.imageNav_n_of_total', { n: imgIdx + 1, total: imageCount })}
                </span>
                <button
                  onClick={() => setImgIdx((i) => Math.min(imageCount - 1, i + 1))}
                  disabled={imgIdx === imageCount - 1}
                  className="rounded border px-2 py-1 disabled:opacity-30"
                >
                  {t('receipt.nextImage')}
                </button>
              </div>
            )}
          </div>
        )}

        {!editing ? (
          <div className="space-y-2 rounded-2xl border bg-card p-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('receipt.merchant')}</span>
              <span>{receipt.merchant ?? '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('receipt.date')}</span>
              <span>{receipt.receipt_date ? formatDate(receipt.receipt_date) : '—'}</span>
            </div>
            {receipt.receipt_time && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('receipt.time')}</span>
                <span className="font-mono">{receipt.receipt_time}</span>
              </div>
            )}
            {receipt.receipt_number && (
              <div className="flex justify-between gap-2 text-sm">
                <span className="text-muted-foreground">{t('receipt.receiptHash')}</span>
                <span className="truncate font-mono text-xs">{receipt.receipt_number}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('receipt.category')}</span>
              <span>{categoryLabel(categoryById(receipt.category_id))}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('receipt.spentBy')}</span>
              <span>{memberLabel(memberById(receipt.spender_user_id))}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('receipt.recordedBy')}</span>
              <span>{memberLabel(memberById(receipt.recorded_by))}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('receipt.subtotal')}</span>
              <span>
                {receipt.subtotal != null ? formatCurrency(receipt.subtotal, currency) : '—'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('receipt.tax')}</span>
              <span>
                {receipt.tax_amount != null ? formatCurrency(receipt.tax_amount, currency) : '—'}
              </span>
            </div>
            <div className="flex justify-between border-t pt-2 font-semibold">
              <span>{t('receipt.total')}</span>
              <span>{formatCurrency(receipt.total_amount, currency)}</span>
            </div>
            {confidencePct != null && (
              <div className="pt-1 text-xs text-muted-foreground">
                {imageCount > 1
                  ? t('receipt.aiConfidencePhotos', {
                      pct: confidencePct,
                      model: receipt.ai_model ?? '—',
                      count: imageCount,
                    })
                  : t('receipt.aiConfidence', {
                      pct: confidencePct,
                      model: receipt.ai_model ?? '—',
                    })}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 rounded-2xl border bg-card p-4">
            <h3 className="text-sm font-semibold">{t('receipt.editReceiptTitle')}</h3>
            <label className="block text-xs">
              <span className="mb-1 block text-muted-foreground">{t('receipt.merchant')}</span>
              <input
                type="text"
                value={form?.merchant ?? ''}
                onChange={(e) => setForm((f) => (f ? { ...f, merchant: e.target.value } : f))}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-muted-foreground">{t('receipt.merchantZh')}</span>
              <input
                type="text"
                value={form?.merchant_zh ?? ''}
                onChange={(e) => setForm((f) => (f ? { ...f, merchant_zh: e.target.value } : f))}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('receipt.dateFormat')}</span>
                <input
                  type="date"
                  value={form?.receipt_date ?? ''}
                  onChange={(e) => setForm((f) => (f ? { ...f, receipt_date: e.target.value } : f))}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('receipt.time')}</span>
                <input
                  type="time"
                  value={form?.receipt_time ?? ''}
                  onChange={(e) => setForm((f) => (f ? { ...f, receipt_time: e.target.value } : f))}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('receipt.category')}</span>
                <select
                  value={form?.category_id ?? ''}
                  aria-label={t('receipt.category')}
                  onChange={(e) =>
                    setForm((f) => (f ? { ...f, category_id: e.target.value } : f))
                  }
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                >
                  <option value="">{t('receipt.categoryNone')}</option>
                  {categories?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.icon ? `${c.icon} ` : ''}
                      {c.name}
                      {c.name_zh ? ` / ${c.name_zh}` : ''}
                    </option>
                  ))}
                </select>
                <CategoryHint category={categories?.find(c=>c.id===form?.category_id)} />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('receipt.spentBy')}</span>
                <select
                  value={form?.spender_user_id ?? ''}
                  onChange={(e) =>
                    setForm((f) => (f ? { ...f, spender_user_id: e.target.value } : f))
                  }
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                >
                  <option value="">{t('receipt.spenderNotSet')}</option>
                  {members?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name?.trim() || m.email}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('receipt.currency')}</span>
                <input
                  type="text"
                  value={form?.currency ?? ''}
                  onChange={(e) =>
                    setForm((f) => (f ? { ...f, currency: e.target.value.toUpperCase().slice(0, 3) } : f))
                  }
                  maxLength={3}
                  placeholder="AUD"
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm uppercase"
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('receipt.subtotal')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={form?.subtotal ?? ''}
                  onChange={(e) => setForm((f) => (f ? { ...f, subtotal: e.target.value } : f))}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('receipt.tax')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={form?.tax_amount ?? ''}
                  onChange={(e) => setForm((f) => (f ? { ...f, tax_amount: e.target.value } : f))}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('receipt.total')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={form?.total_amount ?? ''}
                  onChange={(e) => setForm((f) => (f ? { ...f, total_amount: e.target.value } : f))}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                />
              </label>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => {
                  setEditing(false);
                  setActionError(null);
                }}
                disabled={patchMutation.isPending}
                className="flex-1 rounded-md border py-1.5 text-sm hover:bg-muted disabled:opacity-50"
              >
                {t('receipt.cancelEdit')}
              </button>
              <button
                onClick={saveEdit}
                disabled={patchMutation.isPending}
                className="flex-1 rounded-md bg-primary py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {patchMutation.isPending ? t('receipt.saving') : t('receipt.saveEdit')}
              </button>
            </div>
          </div>
        )}

        <div className="rounded-2xl border bg-card p-4">
          <h3 className="mb-2 font-semibold">{t('receipt.items')} ({items.length})</h3>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {failed ? t('receipt.noItemsFailed') : t('receipt.noItems')}
            </p>
          ) : (
            <ul className="divide-y">
              {items.map((item) => (
                <li key={item.id} className="py-2">
                  {editingItemId === item.id ? (
                    <ItemFormRow
                      initial={item}
                      saveLabel={t('receipt.saveItem')}
                      isPending={updateItemMutation.isPending || deleteItemMutation.isPending}
                      onSave={(data) =>
                        updateItemMutation.mutate({ itemId: item.id, body: data })
                      }
                      onCancel={() => setEditingItemId(null)}
                      onDelete={() => deleteItemMutation.mutate(item.id)}
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0 flex-1 pr-2">
                        <div className="line-clamp-2">{item.name}</div>
                        {item.name_zh && (
                          <div className="line-clamp-2 text-xs text-muted-foreground">
                            {item.name_zh}
                          </div>
                        )}
                        {item.quantity > 1 && (
                          <div className="text-xs text-muted-foreground">
                            {item.quantity} ×{' '}
                            {item.unit_price != null
                              ? formatCurrency(item.unit_price, currency)
                              : '—'}
                          </div>
                        )}
                      </div>
                      <span className={item.is_discount ? 'text-emerald-600' : ''}>
                        {formatCurrency(item.subtotal, currency)}
                      </span>
                      <button
                        onClick={() => {
                          setEditingItemId(item.id);
                          setAddingItem(false);
                          setActionError(null);
                        }}
                        className="rounded p-1 text-muted-foreground hover:bg-muted"
                        aria-label={t('receipt.edit')}
                      >
                        ✏️
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 border-t pt-3">
            {addingItem ? (
              <ItemFormRow
                initial={null}
                saveLabel={t('receipt.saveItem')}
                isPending={addItemMutation.isPending}
                onSave={(data) => addItemMutation.mutate(data)}
                onCancel={() => setAddingItem(false)}
              />
            ) : (
              <button
                onClick={() => {
                  setAddingItem(true);
                  setEditingItemId(null);
                  setActionError(null);
                }}
                className="w-full rounded-md border-2 border-dashed border-muted-foreground/30 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {t('receipt.addItem')}
              </button>
            )}
          </div>
        </div>

        {actionError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {actionError}
          </div>
        )}

        {!editing && (
          <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3 rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted/50">
            <div className="min-w-0 flex-1">
              <div className="font-medium">{t('receipt.taxDeductibleLabel')}</div>
              <div className="text-xs text-muted-foreground">{t('receipt.taxDeductibleHint')}</div>
            </div>
            <input
              type="checkbox"
              checked={receipt.is_tax_deductible === 1}
              disabled={patchMutation.isPending}
              onChange={(e) => {
                setActionError(null);
                patchMutation.mutate({ is_tax_deductible: e.target.checked ? 1 : 0 });
              }}
              className="h-5 w-5"
            />
          </label>
        )}

        {!editing && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={startEdit}
              className="flex-1 rounded-md border bg-card py-2 text-sm font-medium hover:bg-muted"
            >
              {t('receipt.edit')}
            </button>
            <button
              onClick={() => {
                setActionError(null);
                patchMutation.mutate({ is_verified: !receipt.is_verified });
              }}
              disabled={patchMutation.isPending}
              className={
                receipt.is_verified
                  ? 'flex-1 rounded-md border border-emerald-500/40 bg-emerald-50 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50'
                  : 'flex-1 rounded-md border bg-card py-2 text-sm font-medium hover:bg-muted disabled:opacity-50'
              }
            >
              {receipt.is_verified ? t('receipt.markVerifiedUndo') : t('receipt.markVerified')}
            </button>
            {imageCount > 0 && !failed && (
              <button
                onClick={() => setShowImages((v) => !v)}
                className="flex-1 rounded-md border bg-card py-2 text-sm font-medium hover:bg-muted"
              >
                {showImages
                  ? t('receipt.hideOriginals')
                  : imageCount > 1
                    ? t('receipt.viewOriginals', { count: imageCount })
                    : t('receipt.viewOriginal')}
              </button>
            )}
            <button
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="flex-1 rounded-md border border-destructive/40 bg-card py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              {deleteMutation.isPending ? t('receipt.deleting') : t('receipt.deleteReceipt')}
            </button>
          </div>
        )}

        {!editing && (
          <div className="space-y-2 rounded-xl border bg-card p-3">
            <AskAiButton label={t('receipt.askAbout')} />
            <p className="text-xs leading-relaxed text-muted-foreground">{t('receipt.askAboutHint')}</p>
          </div>
        )}

        <div className="text-center">
          <BackButton to="/ledger" />
        </div>
      </main>
      {aliasLearned && (
        <div className="fixed inset-x-0 bottom-safe z-40 flex justify-center px-4">
          <div className="w-full max-w-md rounded-2xl border bg-background p-4 shadow-lg">
            {aliasToastState === 'applied' ? (
              <p className="text-sm">{t('receipt.aliasApplied')}</p>
            ) : (
              <>
                <p className="text-sm font-medium">
                  {t('receipt.aliasLearnedTitle', { name: aliasLearned.display_name })}
                </p>
                {aliasLearned.backfillable_count > 0 ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('receipt.aliasLearnedBackfillable', { count: aliasLearned.backfillable_count })}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('receipt.aliasLearnedAutoApply')}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {aliasLearned.backfillable_count > 0 && (
                    <button
                      disabled={aliasBackfillMutation.isPending}
                      onClick={() => aliasBackfillMutation.mutate()}
                      className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                      {aliasBackfillMutation.isPending ? t('receipt.aliasApplying') : t('receipt.aliasApplyToHistory')}
                    </button>
                  )}
                  <button
                    onClick={() => setAliasLearned(null)}
                    className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
                  >
                    {t('receipt.aliasLater')}
                  </button>
                  <button
                    onClick={() => aliasUndoMutation.mutate()}
                    disabled={aliasUndoMutation.isPending}
                    className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
                  >
                    {t('receipt.aliasUndo')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {aliasPrefill && (
        <AliasFormModal
          mode="create"
          prefill={aliasPrefill}
          onClose={() => setAliasPrefill(null)}
        />
      )}
    </div>
  );
}

